import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ICreateAccount } from '../../account/create-account.helper';
import { ACCOUNT_NAME_MAX, ACCOUNT_NAME_MIN, COMPANY_NAME_MIN } from '../../account/account.validators';

import { conditionalValidator } from '../../validation/validation.helpers';
import { CountriesService } from '../../countries/countries.service';
import { getLogoGroup } from '../image-input/image-input.utils';

/*ToDo: handle serverErrors like in article `https://juristr.com/blog/2019/02/display-server-side-validation-errors-with-angular/`
* Test it in create account wizard
* */
@Component({
  selector: 'app-data-form',
  templateUrl: './data-form.component.html',
  styleUrls: ['./data-form.component.scss'],
})
export class DataFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public withLogoEditor: boolean = true;

  @Input()
  public isReadOnlyLogo: boolean;

  @Input()
  public defaultValues: ICreateAccount;

  @Input()
  public file: string;

  @Output()
  public formChangesEmitter: EventEmitter<any> = new EventEmitter();

  public form: FormGroup;
  public showCorrespondenceAddress = false;

  public readonly ACCOUNT_NAME_MAX = ACCOUNT_NAME_MAX;
  public readonly ACCOUNT_NAME_MIN = ACCOUNT_NAME_MIN;
  public readonly COMPANY_NAME_MIN = COMPANY_NAME_MIN;

  public countriesService: CountriesService;
  private formDestroy$ = new Subject<boolean>();
  private _disableForm = false;
  private fb: FormBuilder;

  constructor(
    fb: FormBuilder,
    countriesService: CountriesService
  ) {
    this.fb = fb;
    this.countriesService = countriesService;
  }

  @Input()
  public set disableForm(disable: boolean) {
    if (disable) {
      this.form?.disable();
    } else {
      this.form?.enable();
    }
    this._disableForm = disable;
  };

  public ngOnInit() {
    if (!this.defaultValues) {
      return;
    }
    this.initComponent();
  }

  public initComponent() {
    this.initForm();
    this.showCorrespondenceAddress = this.form.get('showCorrespondenceAddress')?.value;
  }

  public initForm() {
    this.resetFormSubscription();

    this.form = this.fb.group({
      logo: getLogoGroup(this.fb, this.defaultValues?.logo, this.getFieldValue.bind(this)),
      name: [this.defaultValues?.name,
        [Validators.maxLength(ACCOUNT_NAME_MAX)]],
      companyNIP: [this.defaultValues.companyNIP,
        [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      companyName: [this.defaultValues.companyName,
        [Validators.required, Validators.minLength(COMPANY_NAME_MIN), Validators.maxLength(ACCOUNT_NAME_MAX)]],
      address: this.fb.group({
        country: [this.defaultValues.address?.country,
          [Validators.required]],
        street: [this.defaultValues.address?.street,
          [Validators.required, Validators.minLength(1), Validators.maxLength(40)]],
        houseNumber: [this.defaultValues.address?.houseNumber,
          [Validators.required, Validators.minLength(1), Validators.maxLength(10)]],
        apartmentNumber: [this.defaultValues.address?.apartmentNumber],
        postalCode: [this.defaultValues.address?.postalCode,
          [Validators.required, Validators.minLength(2), Validators.maxLength(15)]],
        city: [this.defaultValues.address?.city,
          [Validators.required, Validators.minLength(2), Validators.maxLength(40)]],
      }),
      mobilePhone: [this.defaultValues.mobilePhone,
        [Validators.required, Validators.minLength(5), Validators.maxLength(20)]],
      email: [this.defaultValues.email,
        [Validators.required, Validators.email]],
      showCorrespondenceAddress: [this.defaultValues.showCorrespondenceAddress],
      correspondenceAddress: this.fb.group({
        country: [
          this.defaultValues.correspondenceAddress?.country,
          [conditionalValidator(
            () => this.getFieldValue('showCorrespondenceAddress'),
            Validators.required
          )]
        ],
        street: [
          this.defaultValues.correspondenceAddress?.street,
          [conditionalValidator(
            () => this.getFieldValue('showCorrespondenceAddress'),
            Validators.compose([
              Validators.required, Validators.minLength(1), Validators.maxLength(40)
            ])
          )]
        ],
        houseNumber: [
          this.defaultValues.correspondenceAddress?.houseNumber,
          [conditionalValidator(
            () => this.getFieldValue('showCorrespondenceAddress'),
            Validators.compose([
              Validators.required, Validators.minLength(1), Validators.maxLength(10)
            ])
          )]
        ],
        apartmentNumber: [this.defaultValues.correspondenceAddress?.apartmentNumber],
        postalCode: [
          this.defaultValues.correspondenceAddress?.postalCode,
          [conditionalValidator(
            () => this.getFieldValue('showCorrespondenceAddress'),
            Validators.compose([
              Validators.required, Validators.minLength(2), Validators.maxLength(15)
            ])
          )]
        ],
        city: [
          this.defaultValues.correspondenceAddress?.city,
          [conditionalValidator(
            () => this.getFieldValue('showCorrespondenceAddress'),
            Validators.compose([
              Validators.required, Validators.minLength(2), Validators.maxLength(40)
            ])
          )]
        ],
      }),
    });

    this.form.get('showCorrespondenceAddress')?.valueChanges
      .pipe(
        takeUntil(this.formDestroy$)
      )
      .subscribe(() => {
        this.form.get('correspondenceAddress.country')?.updateValueAndValidity();
        this.form.get('correspondenceAddress.street')?.updateValueAndValidity();
        this.form.get('correspondenceAddress.houseNumber')?.updateValueAndValidity();
        this.form.get('correspondenceAddress.postalCode')?.updateValueAndValidity();
        this.form.get('correspondenceAddress.city')?.updateValueAndValidity();
      });

    this.form.valueChanges
      .pipe(
        takeUntil(this.formDestroy$)
      )
      .subscribe(val => {
        this.formChangesEmitter.emit({
          val: val,
          isFormValid: this.form.valid
        })
      });

    if (this._disableForm) {
      this.form.disable();
    }
  }

  public hasError(form: FormGroup, control: string, errorCodes: string[]) {
    return errorCodes.some(errorCode => form.get(control)?.hasError(errorCode))
  }

  public isTouchedAndHasError(form: FormGroup, control: string, errorCodes: string[]) {
    if (!form.get(control)?.touched) {
      return false;
    }

    return this.hasError(form, control, errorCodes);
  }

  public onChangeOtherAddress() {
    this.showCorrespondenceAddress = this.form.get('showCorrespondenceAddress')?.value;
  }

  public ngOnDestroy() {
    this.clearCurrentFormSubscribers();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.defaultValues && !changes.defaultValues.firstChange) {
      this.initComponent();
    }
  }

  private resetFormSubscription() {
    this.clearCurrentFormSubscribers();
    this.formDestroy$ = new Subject();
  }

  private clearCurrentFormSubscribers() {
    this.formDestroy$.next(true);
    this.formDestroy$.complete();
  }

  private getFieldValue(name: string) {
    return this.form.get(name)?.value;
  }
}
